import React, { useState } from 'react';
import { 
  getOrganizationLogo, 
  getDefaultLogo, 
  getLogoSizeClass,
  OrganizationLogoProps 
} from '@/utils/organizationUtils';

/**
 * OrganizationLogo Component
 * 
 * A reusable component that displays organization logos with automatic fallbacks.
 * Supports both SVG and PNG formats, theme-aware styling, and multiple size presets.
 * 
 * Features:
 * - Automatic fallback from organization logo to default Wrestling AI logo
 * - Theme-aware (dark/light mode support)
 * - Multiple size presets (small, medium, large) or custom sizing
 * - Error handling with graceful fallbacks
 * - Extensible to any organization name
 */
const OrganizationLogo: React.FC<OrganizationLogoProps> = ({
  organizationName,
  isDark = false,
  className = '',
  size = 'medium',
  onClick
}) => {
  const [hasError, setHasError] = useState(false);
  const [currentFormat, setCurrentFormat] = useState<'svg' | 'png'>('svg');

  // Debug logging
  console.log('OrganizationLogo - organizationName:', organizationName);

  // Get logo paths based on organization name
  const logoData = organizationName 
    ? getOrganizationLogo(organizationName, isDark)
    : getDefaultLogo(isDark);

  // Build CSS classes
  const sizeClass = getLogoSizeClass(size);
  const baseClasses = `${sizeClass} object-contain transition-opacity duration-200`;
  const combinedClasses = `${baseClasses} ${className}`.trim();

  /**
   * Handle image load errors by falling back through formats and then to default logo
   */
  const handleImageError = () => {
    console.log('OrganizationLogo - Image load failed for:', getImageSource());
    
    if (organizationName && !hasError) {
      if (currentFormat === 'svg') {
        // Try PNG format first
        setCurrentFormat('png');
      } else {
        // Both organization formats failed, use default logo
        setHasError(true);
        setCurrentFormat('svg');
      }
    } else if (currentFormat === 'svg') {
      // Default SVG failed, try default PNG
      setCurrentFormat('png');
    }
  };

  /**
   * Get the appropriate image source based on current state
   */
  const getImageSource = (): string => {
    if (organizationName && !hasError) {
      return currentFormat === 'svg' ? logoData.svg : logoData.png;
    } else {
      return currentFormat === 'svg' ? logoData.fallbackSvg : logoData.fallbackPng;
    }
  };

  /**
   * Get the appropriate alt text
   */
  const getAltText = (): string => {
    if (organizationName && !hasError) {
      return logoData.alt;
    }
    return "Wrestling Team";
  };

  return (
    <img
      src={getImageSource()}
      alt={getAltText()}
      className={combinedClasses}
      onError={handleImageError}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      title={getAltText()}
    />
  );
};

export default OrganizationLogo; 